import { filterClasses } from "@swivl-mono/helpers";
import { ReactNode, useEffect, useRef } from "react";
import { IoMdCheckmark } from "react-icons/io";

import { Checkbox } from "../Checkbox/Checkbox";
import { highlightText } from "../Helpers/HighlightText";
import { Spinner } from "../Spinner/Spinner";
import { SelectItem, SelectOption, isDivider, isOptionGroup, isSelectOption } from "./Select";

interface OptionsListProps {
  items: SelectItem[];
  loading?: boolean;
  searchQuery: string;
  selectedValues?: string[];
  handleToggleOption: (option: string) => void;
  isMultiSelect: boolean;
  emptyStateNote?: ReactNode;
}

const OptionsList = ({
  items,
  loading = false,
  searchQuery,
  selectedValues,
  handleToggleOption,
  isMultiSelect,
  emptyStateNote
}: OptionsListProps) => {
  const listRef = useRef<HTMLDivElement>(null);

  // Attach a native wheel listener to stop propagation before it reaches
  // parent scroll-lock handlers (e.g. react-remove-scroll in Radix Dialog).
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;

    const handleWheel = (e: WheelEvent): void => {
      if (el.scrollHeight > el.clientHeight) {
        e.stopPropagation();
      }
    };

    el.addEventListener("wheel", handleWheel);
    return () => el.removeEventListener("wheel", handleWheel);
  }, []);

  const renderOption = (option: SelectOption) => {
    const isSelected = selectedValues?.includes(option.value);
    const isDisabled = option.disabled ?? false;
    const highlightedText = highlightText(option.label, searchQuery);
    const labelColumn = (
      <span className="flex min-w-0 flex-col">
        <span className="text-sm">{highlightedText}</span>
        {option.sublabel && <span className="truncate text-xs text-grey-600">{highlightText(option.sublabel, searchQuery)}</span>}
      </span>
    );

    return (
      <div
        aria-disabled={isDisabled}
        aria-selected={isSelected}
        className={filterClasses(
          "flex w-full items-center gap-3 rounded-sm px-2 py-2 transition-colors",
          isDisabled ? "cursor-not-allowed text-grey-500" : "cursor-pointer hover:bg-grey-50",
          isSelected && !isMultiSelect && !isDisabled && "text-orange-500"
        )}
        key={option.value}
        onClick={isDisabled ? undefined : () => handleToggleOption(option.value)}
        role="option"
      >
        {isMultiSelect && <Checkbox checked={isSelected} name={option.value} />}
        {option.icon ? (
          <span className="flex min-w-0 items-center gap-2">
            <span className="flex shrink-0 items-center">{option.icon}</span>
            {labelColumn}
          </span>
        ) : (
          labelColumn
        )}
        {!isMultiSelect && <IoMdCheckmark className={filterClasses("ml-auto size-4 text-orange-500", !isSelected && "hidden")} />}
      </div>
    );
  };

  const renderItems = () => {
    if (loading) {
      return (
        <div className="w-full px-2 py-2">
          <div className="flex w-full items-center justify-center rounded-lg bg-white py-4 text-base text-grey-500">
            <Spinner />
          </div>
        </div>
      );
    }

    if (items.length === 0) {
      return (
        <div className="w-full px-4 py-3 text-center text-sm text-grey-600">
          <div>{searchQuery.length === 0 ? "No options found" : "No results"}</div>
          {emptyStateNote && <div className="body-sm mt-1 text-grey-600">{emptyStateNote}</div>}
        </div>
      );
    }

    return items.map((item, index) => {
      if (isDivider(item)) {
        return (
          <div key={`divider-${index}`} className="my-1 px-2">
            <div className="border-t border-grey-300" />
            {item.label && <div className="px-2 py-1 text-xs font-medium tracking-wide text-grey-500 uppercase">{item.label}</div>}
          </div>
        );
      }

      if (isOptionGroup(item)) {
        return (
          <div key={`group-${item.label || index}`} className="py-1">
            {item.label && <div className="body-sm px-2 py-2 text-grey-600">{item.label}</div>}
            <div>{item.options.map((option) => renderOption(option))}</div>
          </div>
        );
      }

      if (isSelectOption(item)) {
        return renderOption(item);
      }

      return null;
    });
  };

  return (
    <div ref={listRef} className="mb-1 min-h-0 w-full overflow-y-auto">
      {renderItems()}
    </div>
  );
};

export { OptionsList };
